import { zodResolver } from '@hookform/resolvers/zod';
import { IonContent } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Mail } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import {
  AUTH_FIELD_LABEL_CLASS,
  AuthLayoutWrapper,
} from '@/core/components/auth/auth-layout-wrapper';
import { UsernameInput } from '@/core/components/auth/username-input';
import { useAuthFlow } from '@/core/hooks/use-auth-flow';
import { useAuthPublicConfig } from '@/core/hooks/use-auth-public-config';
import { AppIonPage } from '@/core/components/app-ion-page';

export function RegisterPage() {
  const location = useLocation();
  const router = useIonRouter();
  const { sendRegisterCode, commonDict, authRegisterDict, errorsDict } =
    useAuthFlow();
  const { registrationEnabled } = useAuthPublicConfig();

  // Get email from URL search params
  const searchParams = new URLSearchParams(location.search);
  const emailFromParams = searchParams?.get('email') || '';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(errorsDict.invalidEmail),
        firstName: z.string().min(1, errorsDict.invalidFirstName),
        lastName: z.string().min(1, errorsDict.invalidLastName),
        username: z.string().optional(),
      }),
    [
      errorsDict.invalidEmail,
      errorsDict.invalidFirstName,
      errorsDict.invalidLastName,
    ],
  );

  type RegisterFormData = z.infer<typeof registerSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<RegisterFormData>({
    mode: 'onTouched',
    resolver: zodResolver(registerSchema as never),
    defaultValues: {
      email: emailFromParams,
      firstName: '',
      lastName: '',
      username: '',
    },
  });

  // Use useWatch instead of watch() to avoid React Compiler warnings
  const firstNameValue = useWatch({ control, name: 'firstName' });
  const lastNameValue = useWatch({ control, name: 'lastName' });
  const emailValue = useWatch({ control, name: 'email' });
  const usernameValue = useWatch({ control, name: 'username' });

  const onSubmit = handleSubmit(async (values) => {
    setIsLoading(true);
    setError(null);

    const result = await sendRegisterCode(
      values.email,
      values.firstName,
      values.lastName,
      values.username,
    );

    if (result.success) {
      router.push(
        `/auth/verify?email=${encodeURIComponent(values.email)}`,
        'forward',
        'push',
      );
    } else {
      // Show error
      setError(result.error || errorsDict.requestFailed);
      setIsLoading(false);
    }
  });

  return (
    <AppIonPage>
      <IonContent className="ion-padding" fullscreen scrollY={false}>
        <div className="bg-background text-foreground flex h-full w-full safe-area-padding">
          <AuthLayoutWrapper title={authRegisterDict.title} backTo="/auth">
            {registrationEnabled === false ? (
              <div className="flex flex-col gap-5">
                <Alert>
                  <AlertDescription>
                    {authRegisterDict.registrationsDisabled}
                  </AlertDescription>
                </Alert>
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  radius="full"
                  className="w-full"
                  onClick={() => router.push('/auth', 'back', 'replace')}
                >
                  {authRegisterDict.loginLink}
                </Button>
              </div>
            ) : (
              <>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={onSubmit} className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="firstName"
                      className={AUTH_FIELD_LABEL_CLASS}
                    >
                      {commonDict.firstNameLabel}
                    </label>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                          id="firstName"
                          type="text"
                          autoComplete="given-name"
                          placeholder={commonDict.firstNamePlaceholder}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    {errors.firstName && (
                      <p className="text-[13.5px] text-destructive">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="lastName"
                      className={AUTH_FIELD_LABEL_CLASS}
                    >
                      {commonDict.lastNameLabel}
                    </label>
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                          id="lastName"
                          type="text"
                          autoComplete="family-name"
                          placeholder={commonDict.lastNamePlaceholder}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    {errors.lastName && (
                      <p className="text-[13.5px] text-destructive">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="username"
                      className={AUTH_FIELD_LABEL_CLASS}
                    >
                      {commonDict.usernameLabel}
                    </label>
                    <UsernameInput
                      id="username"
                      value={usernameValue || ''}
                      onChange={(value) => setValue('username', value)}
                      firstName={firstNameValue}
                      lastName={lastNameValue}
                      onProposalSelect={(value) => setValue('username', value)}
                      error={errors.username?.message}
                      disabled={isLoading}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className={AUTH_FIELD_LABEL_CLASS}>
                      {commonDict.emailLabel}
                    </label>
                    <div className="relative">
                      <Mail
                        className="pointer-events-none absolute left-3 top-3 z-10 h-4 w-4 text-muted-foreground"
                        aria-hidden
                      />
                      <Controller
                        control={control}
                        name="email"
                        render={({ field: { value } }) => (
                          <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            placeholder={commonDict.emailPlaceholder}
                            className="pl-10"
                            value={value}
                            disabled
                          />
                        )}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    radius="full"
                    className="w-full"
                    disabled={
                      !emailValue ||
                      emailValue.length === 0 ||
                      !firstNameValue ||
                      !lastNameValue
                    }
                    isLoading={isLoading}
                  >
                    {commonDict.sendCode}
                  </Button>
                </form>
              </>
            )}
          </AuthLayoutWrapper>
        </div>
      </IonContent>
    </AppIonPage>
  );
}
